/**
 * Time Range Utilities for Data Adjustments
 * 
 * Provides optimized time range extraction and overlap validation:
 * - Chunked reading for large files (only first/last 50KB)
 * - Multi-file overlap detection
 * - Outlier file identification
 */

import type { TFunction } from 'i18next';
import type { FileTimeRange, FileValidationResult } from '../types/dataAdjustments.types';
import { CSV_CHUNK_SIZE } from '../types/dataAdjustments.types';
import { detectCSVSeparator } from './csvValidators';

/**
 * Time column names to search for in CSV headers
 */
const TIME_COLUMN_NAMES = ['utc', 'timestamp', 'time', 'datetime', 'date', 'zeit'];

/**
 * Extract time range from CSV file (min and max UTC timestamps)
 * OPTIMIZED: Only reads first ~50KB for header+start, and last ~50KB for end
 * This avoids loading entire large files into browser memory
 */
export const extractTimeRangeFromCSV = async (file: File): Promise<FileTimeRange> => {
  try {
    // Read first chunk for header and first timestamps
    const firstChunk = await file.slice(0, CSV_CHUNK_SIZE).text();

    if (!firstChunk || firstChunk.trim() === '') {
      return { filename: file.name, minTime: null, maxTime: null, error: 'empty' };
    }

    const firstLines = firstChunk.split(/\r?\n/).filter(Boolean);

    if (firstLines.length < 2) {
      return { filename: file.name, minTime: null, maxTime: null, error: 'no_data' };
    }

    // Auto-detect separator from header
    const sep = detectCSVSeparator(firstLines[0]);
    const headers = firstLines[0].split(sep).map((h) => h.trim().toLowerCase());

    // Find time column
    const timeIndex = headers.findIndex((h) => TIME_COLUMN_NAMES.includes(h));

    if (timeIndex === -1) {
      return { filename: file.name, minTime: null, maxTime: null, error: 'no_time_column' };
    }

    // Parse first valid timestamp (skip header, line index 1)
    let minTime: Date | null = null;
    for (let i = 1; i < Math.min(firstLines.length, 100); i++) {
      const parts = firstLines[i].split(sep);
      if (parts.length > timeIndex && parts[timeIndex]?.trim()) {
        const parsed = new Date(parts[timeIndex].trim());
        if (!isNaN(parsed.getTime())) {
          minTime = parsed;
          break;
        }
      }
    }

    if (!minTime) {
      return { filename: file.name, minTime: null, maxTime: null, error: 'no_valid_timestamps' };
    }

    // Read last chunk for last timestamps
    let maxTime: Date | null = null;
    const fileSize = file.size;

    if (fileSize > CSV_CHUNK_SIZE) {
      const lastChunk = await file.slice(fileSize - CSV_CHUNK_SIZE).text();
      const lastLines = lastChunk.split(/\r?\n/).filter(Boolean);

      // Parse from end backwards to find last valid timestamp
      for (let i = lastLines.length - 1; i >= 0; i--) {
        const parts = lastLines[i].split(sep);
        if (parts.length > timeIndex && parts[timeIndex]?.trim()) {
          const parsed = new Date(parts[timeIndex].trim());
          if (!isNaN(parsed.getTime())) {
            maxTime = parsed;
            break;
          }
        }
      }
    } else {
      // Small file - use data from first chunk
      for (let i = firstLines.length - 1; i >= 1; i--) {
        const parts = firstLines[i].split(sep);
        if (parts.length > timeIndex && parts[timeIndex]?.trim()) {
          const parsed = new Date(parts[timeIndex].trim());
          if (!isNaN(parsed.getTime())) {
            maxTime = parsed;
            break;
          }
        }
      }
    }

    // If no maxTime found, use minTime (single data point)
    if (!maxTime) {
      maxTime = minTime;
    }

    // Ensure minTime <= maxTime (swap if data is in reverse order)
    if (minTime > maxTime) {
      [minTime, maxTime] = [maxTime, minTime];
    }

    return { filename: file.name, minTime, maxTime };
  } catch {
    return { filename: file.name, minTime: null, maxTime: null, error: 'read_error' };
  }
};

/**
 * Format date for display (YYYY-MM-DD)
 */
const formatDateShort = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Validate time overlap between multiple files BEFORE upload
 * Returns validation result with error message identifying outlier file(s)
 */
export const validateTimeOverlap = async (
  files: File[],
  t: TFunction
): Promise<FileValidationResult> => {
  if (files.length < 2) {
    return { isValid: true };
  }

  // Extract time ranges from all files
  const timeRanges = await Promise.all(files.map(extractTimeRangeFromCSV));

  // Check for extraction errors
  const failedFiles = timeRanges.filter((r) => r.error);
  if (failedFiles.length > 0) {
    const errorFile = failedFiles[0];
    let errorKey = 'errors.timeRangeExtractionFailed';
    if (errorFile.error === 'no_time_column') {
      errorKey = 'errors.missingTimeColumn';
    }
    return {
      isValid: false,
      error: t(errorKey, { filename: errorFile.filename }),
    };
  }

  // Check for valid time ranges
  const validRanges = timeRanges.filter((r) => r.minTime && r.maxTime);
  if (validRanges.length !== files.length) {
    return {
      isValid: false,
      error: t('errors.invalidTimeRanges'),
    };
  }

  // Calculate overall overlap
  const latestStart = new Date(Math.max(...validRanges.map((r) => r.minTime!.getTime())));
  const earliestEnd = new Date(Math.min(...validRanges.map((r) => r.maxTime!.getTime())));

  // Check if overlap exists
  if (latestStart > earliestEnd) {
    // Find the outlier file(s) - the one(s) that don't overlap with majority

    if (files.length === 2) {
      // For 2 files, show both
      const f1 = validRanges[0];
      const f2 = validRanges[1];
      return {
        isValid: false,
        error: `${t('errors.noOverlapShort')}:\n• "${f1.filename}" (${formatDateShort(f1.minTime!)} - ${formatDateShort(f1.maxTime!)})\n• "${f2.filename}" (${formatDateShort(f2.minTime!)} - ${formatDateShort(f2.maxTime!)})`,
      };
    }

    // For 3+ files, find the outlier that doesn't overlap with others
    // Calculate overlap excluding each file to find which one is the problem
    let outlierFile = validRanges[0];
    let maxOverlapWithout = -1;

    for (let i = 0; i < validRanges.length; i++) {
      const othersRanges = validRanges.filter((_, idx) => idx !== i);
      const othersLatestStart = Math.max(...othersRanges.map((r) => r.minTime!.getTime()));
      const othersEarliestEnd = Math.min(...othersRanges.map((r) => r.maxTime!.getTime()));
      const overlapDuration = othersEarliestEnd - othersLatestStart;

      if (overlapDuration > maxOverlapWithout) {
        maxOverlapWithout = overlapDuration;
        outlierFile = validRanges[i];
      }
    }

    return {
      isValid: false,
      error: `${t('errors.noOverlapShort')}: "${outlierFile.filename}"\n(${formatDateShort(outlierFile.minTime!)} - ${formatDateShort(outlierFile.maxTime!)})`,
    };
  }

  return { isValid: true };
};

/**
 * Calculate overlapping time range from multiple file ranges
 * Returns the intersection of all time ranges
 */
export const calculateOverlappingRange = (
  ranges: Array<{ start: Date; end: Date }>
): { start: Date; end: Date } | null => {
  if (ranges.length === 0) return null;

  const latestStart = new Date(Math.max(...ranges.map((r) => r.start.getTime())));
  const earliestEnd = new Date(Math.min(...ranges.map((r) => r.end.getTime())));

  if (latestStart <= earliestEnd) {
    return { start: latestStart, end: earliestEnd };
  }

  return null;
};
